import type { ReactNode } from 'react';
import { Button } from '@/components/Button/Button';
import { EmptyState } from '@/components/EmptyState/EmptyState';
import { Spinner } from '@/components/Spinner/Spinner';
import type { AsyncState } from '@/lib/hooks/useAsync';

interface AsyncBoundaryProps<T> {
  state: AsyncState<T>;
  /** What this screen is waiting for, e.g. "cohorts". Used in copy. */
  subject: string;
  /** Issue or epic this endpoint is tracked under, shown on a 501. */
  plannedUnder?: string;
  onRetry?: () => void;
  children: (data: T) => ReactNode;
}

/**
 * Renders the four non-success outcomes of a load consistently.
 *
 * The denied copy deliberately says nothing about *why*: the server returns an
 * identical denial for unauthenticated, under-privileged, and non-existent
 * (BR-AP1), and the UI must not narrow that down either.
 */
export function AsyncBoundary<T>({
  state,
  subject,
  plannedUnder,
  onRetry,
  children,
}: AsyncBoundaryProps<T>) {
  switch (state.status) {
    case 'loading':
      return <Spinner label={`Loading ${subject}`} />;

    case 'denied':
      return (
        <EmptyState
          title="Access denied"
          description={`Your session is not permitted to view ${subject}. If that looks wrong, ask a platform owner to check your role assignment.`}
        />
      );

    case 'not-implemented':
      return (
        <EmptyState
          title="Not built yet"
          description={
            <>
              The API endpoint behind {subject} is scaffolded but not implemented.
              {plannedUnder ? ` Tracked in ${plannedUnder}.` : ''}
            </>
          }
        />
      );

    case 'error':
      return (
        <EmptyState
          tone="critical"
          title={`Could not load ${subject}`}
          description={state.message}
          action={
            onRetry ? (
              <Button size="sm" onClick={onRetry}>
                Try again
              </Button>
            ) : undefined
          }
        />
      );

    case 'ready':
      return <>{children(state.data)}</>;
  }
}
